import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ScheduledTestModal from "./ScheduledTestModal";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Calendar,
  Views,
  DateLocalizer,
  momentLocalizer,
} from "react-big-calendar";
import * as dates from "./dates";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  fetchScheduledTestsForUser,
  fetchTests,
} from "../../../../ApiLayer/scheduledTestProvider";

const mLocalizer = momentLocalizer(moment);

const ColoredDateCellWrapper = ({ children }) =>
  React.cloneElement(React.Children.only(children), {
    style: { backgroundColor: "lightblue" },
  });

const TestsCalendar = ({
  localizer = mLocalizer,
  showDemoLink = true,
  userId,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentTest, setCurrentTest] = useState(null);
  const [data, setData] = useState([]);
  const [editId, setEditId] = useState(null);
  const [tests, setTests] = useState([]);
  const [currentView, setCurrentView] = useState(Views.MONTH);
  const [currentDate, setCurrentDate] = useState(new Date());
  const testColorMapRef = useRef({});

  // Calculate date range for agenda view based on all events
  const agendaRange = useMemo(() => {
    if (!data || data.length === 0) {
      return { start: new Date(), end: dates.add(new Date(), 30, "day") };
    }

    const allDates = data.flatMap((event) => [
      new Date(event.start),
      new Date(event.end),
    ]);
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));

    // Add some padding
    return {
      start: dates.add(minDate, -7, "day"),
      end: dates.add(maxDate, 7, "day"),
    };
  }, [data]);

  // Calculate the default date to be the earliest event date
  const agendaDefaultDate = useMemo(() => {
    if (!data || data.length === 0) {
      return new Date();
    }
    const allStartDates = data.map((event) => new Date(event.start));
    return new Date(Math.min(...allStartDates));
  }, [data]);

  const { components, defaultDate, max, views } = useMemo(
    () => ({
      components: {
        timeSlotWrapper: ColoredDateCellWrapper,
      },
      defaultDate: new Date(),
      max: dates.add(dates.endOf(new Date(2023, 17, 1), "day"), -1, "hours"),
      views: Object.keys(Views).map((k) => Views[k]),
    }),
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchTests();
        const updatedMap = {};
        data.forEach((test) => {
          updatedMap[test.id] = test.color;
        });
        testColorMapRef.current = updatedMap;
        setTests(data);
      } catch (error) {
        console.error("Error fetching tests:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const events = await fetchScheduledTestsForUser(
          userId,
          testColorMapRef.current
        );
        setData(events);
      } catch (error) {
        console.error("Error fetching scheduled tests:", error);
      }
    };
    fetch();
  }, [userId, open, openAddDialog, openEditDialog]);

  const handleEventSelect = (event) => {
    setCurrentTest(event);
    setEditId(event.id);
    setOpenEditDialog(true);
  };

  const onEmptySlotClick = (event) => {
    setCurrentTest(event);
    setOpenAddDialog(true);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setData([]);
    setOpen(false);
  };

  const handleAdd = () => {
    setOpenAddDialog(true);
  };

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleViewChange = (newView) => {
    setCurrentView(newView);
  };

  // Determine which date and navigation handler to use based on view
  const calendarDate =
    currentView === Views.AGENDA ? agendaDefaultDate : currentDate;
  const calendarNavigate =
    currentView === Views.AGENDA ? () => {} : handleNavigate;

  return (
    <div>
      <button
        type="button"
        className="btn btn-info"
        style={{ zIndex: 1, marginRight: "5px" }}
        onClick={() => handleClickOpen()}
      >
        Tests Calendar
      </button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        fullWidth={"true"}
        maxWidth={"lg"}
      >
        <DialogTitle id="alert-dialog-title" style={{ position: "relative" }}>
          {"Tests Calendar"}
          <IconButton
            style={{ position: "absolute", right: 0, top: 0 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ textAlign: "center" }}>
          <IconButton
            style={{ position: "absolute", left: 0, top: 0, color: "#AAFF00" }}
            onClick={handleAdd}
            size="medium"
          >
            <AddIcon style={{ fontSize: "1.5em" }} />
          </IconButton>
          <div
            style={{ height: "85vh", overflow: "auto" }}
            className="height600"
            {...props}
          >
            <Calendar
              onSelectEvent={handleEventSelect}
              onSelectSlot={onEmptySlotClick}
              selectable
              components={components}
              defaultDate={defaultDate}
              date={calendarDate}
              onNavigate={calendarNavigate}
              view={currentView}
              onView={handleViewChange}
              events={data || []}
              localizer={localizer}
              max={max}
              showMultiDayTimes
              step={60}
              views={views}
              length={
                Math.ceil(
                  (agendaRange.end - agendaRange.start) / (1000 * 60 * 60 * 24)
                ) || 730
              }
              eventPropGetter={(event, start, end, isSelected) => {
                const style = {
                  backgroundColor: event.color,
                  borderColor: event.color,
                };
                return { style };
              }}
              slotPropGetter={(date) => {
                return {
                  className: "transparent-slot",
                };
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
      {openEditDialog && (
        <ScheduledTestModal
          isOpen={openEditDialog}
          handleClose={() => {
            setEditId(null);
            setOpenEditDialog(false);
            setCurrentTest(null);
          }}
          tests={tests}
          testData={currentTest}
          editId={editId}
          userId={userId}
        />
      )}
      {openAddDialog && (
        <ScheduledTestModal
          isOpen={openAddDialog}
          handleClose={() => {
            setOpenAddDialog(false);
            setCurrentTest(null);
          }}
          testData={currentTest}
          tests={tests}
          editId={null}
          userId={userId}
        />
      )}
    </div>
  );
};

TestsCalendar.propTypes = {
  localizer: PropTypes.instanceOf(DateLocalizer),
  showDemoLink: PropTypes.bool,
};

export default TestsCalendar;
